import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Typography, Grid, Card, CardContent, Button, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Chip, IconButton,
    Stack, TextField, InputAdornment, Skeleton, Tooltip, Dialog, DialogTitle,
    DialogContent, DialogActions, Select, MenuItem, FormControl, InputLabel,
    Divider, Alert, Snackbar, Switch, Tabs, Tab
} from '@mui/material';
import {
    Search as SearchIcon, ArrowBack as BackIcon, Add as AddIcon,
    Delete as DeleteIcon, Send as SendIcon, Wifi as MqttIcon,
    WifiOff as MqttOffIcon, Sensors as SensorIcon, Refresh as RefreshIcon,
    Code as CodeIcon, Close as CloseIcon, CheckCircle as SuccessIcon,
    Error as ErrorIcon, ContentCopy as CopyIcon
} from '@mui/icons-material';
import deviceConfigService from '../../services/deviceConfigService';
import RawCanFilterBuilder from './deviceConfig/RawCanFilterBuilder';
import DeviceCommandPanel from './deviceConfig/DeviceCommandPanel';
import DeviceCommandConsole from './deviceConfig/DeviceCommandConsole';
import { machineLabel } from '../../utils/machineLabel';

// ─── DEVICE LIST VIEW ────────────────────────────────────────────────
const DeviceListView = ({ devices, loading, onSelect, onRefresh, brokerStatus }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('ALL');

    const deviceTypes = useMemo(() => {
        const types = new Set(devices.map(d => d.device_type).filter(Boolean));
        return ['ALL', ...types];
    }, [devices]);

    const filtered = useMemo(() => {
        let list = devices;
        if (filterType !== 'ALL') list = list.filter(d => d.device_type === filterType);
        if (searchTerm.trim()) {
            const s = searchTerm.toLowerCase();
            list = list.filter(d =>
                d.serial_number?.toLowerCase().includes(s) ||
                d.equipment_id?.toLowerCase().includes(s) ||
                d.device_id?.toLowerCase().includes(s) ||
                d.brand?.toLowerCase().includes(s)
            );
        }
        return list;
    }, [devices, filterType, searchTerm]);

    return (
        <Box>
            {/* MQTT Status Banner */}
            <Alert 
                severity={brokerStatus?.logInaccessible ? 'warning' : (brokerStatus?.running ? 'success' : 'error')} 
                icon={brokerStatus?.running ? <MqttIcon /> : <MqttOffIcon />}
                sx={{ mb: 3, borderRadius: '16px', fontWeight: 600 }}>
                {brokerStatus?.logInaccessible 
                    ? `Mosquitto Broker Running (Log Inaccessible) — Status estimated from telemetry. Error: ${brokerStatus?.error || 'Permission denied'}`
                    : (brokerStatus?.running
                        ? `Mosquitto Broker Running — ${brokerStatus.connectedClients || 0} client(s) connected (from broker log)`
                        : `Mosquitto Broker Offline — ${brokerStatus?.error || 'Broker is not responding'}`)}
            </Alert>

            {/* Search & Filter */}
            <Stack direction="row" spacing={2} mb={3} flexWrap="wrap" useFlexGap>
                <TextField placeholder="Search by Equipment ID, Device ID, Brand..."
                    value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
                    sx={{ flex: 1, minWidth: 280, '& .MuiOutlinedInput-root': { borderRadius: '16px', bgcolor: 'white', fontWeight: 600 } }} />
                <FormControl sx={{ minWidth: 160 }}>
                    <InputLabel>Type</InputLabel>
                    <Select value={filterType} onChange={e => setFilterType(e.target.value)} label="Type"
                        sx={{ borderRadius: '16px', bgcolor: 'white' }}>
                        {deviceTypes.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                    </Select>
                </FormControl>
                <Button startIcon={<RefreshIcon />} variant="outlined" onClick={onRefresh}
                    sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 600 }}>Refresh</Button>
            </Stack>

            {/* Stats */}
            <Grid container spacing={2} mb={3}>
                {[
                    { label: 'Total Devices', value: devices.length, color: '#3b82f6' },
                    { label: 'MQTT Clients', value: brokerStatus?.connectedClients || 0, color: '#22c55e' },
                    { label: 'Broker Status', value: brokerStatus?.running ? 'Running' : 'Offline', color: brokerStatus?.running ? '#22c55e' : '#ef4444' },
                    { label: 'Backend ↔ Broker', value: brokerStatus?.backendConnected ? 'Connected' : 'Offline', color: brokerStatus?.backendConnected ? '#22c55e' : '#ef4444' }
                ].map((s, i) => (
                    <Grid item xs={6} md={3} key={i}>
                        <Card sx={{ borderLeft: `4px solid ${s.color}`, borderRadius: '16px' }}>
                            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.65rem' }}>{s.label}</Typography>
                                <Typography variant="h5" sx={{ fontWeight: 800 }}>{loading ? <Skeleton width={40} /> : s.value}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Devices Table */}
            <TableContainer component={Card} sx={{ borderRadius: '20px' }}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                            {['SERIAL NUMBER', 'DEVICE ID', 'TYPE', 'MQTT', 'STATUS', 'LAST DATA', 'ACTION'].map(h => (
                                <TableCell key={h} sx={{ fontWeight: 700, fontSize: '11px', color: 'text.secondary' }}>{h}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? [...Array(4)].map((_, i) => (
                            <TableRow key={i}><TableCell colSpan={7}><Skeleton height={50} /></TableCell></TableRow>
                        )) : filtered.length > 0 ? filtered.map(d => (
                            <TableRow key={d.device_id} hover sx={{ cursor: 'pointer' }} onClick={() => onSelect(d.device_id)}>
                                <TableCell sx={{ fontWeight: 800, color: 'primary.main', fontFamily: 'monospace' }}>{machineLabel(d)}</TableCell>
                                <TableCell><Typography variant="caption" sx={{ fontWeight: 600, fontFamily: 'monospace', color: 'text.secondary' }}>{d.device_id}</Typography></TableCell>
                                <TableCell><Chip label={d.device_type} size="small" sx={{ fontWeight: 700, borderRadius: '6px', fontSize: '10px' }} /></TableCell>
                                <TableCell>
                                    <Tooltip title={d.mqttConnected ? `IP: ${d.mqttIp || 'N/A'} | Last: ${d.mqttLastSeen ? new Date(d.mqttLastSeen).toLocaleString() : 'N/A'}` : (d.mqttLastAction === 'disconnected' ? `Disconnected at ${d.mqttLastSeen ? new Date(d.mqttLastSeen).toLocaleString() : 'N/A'}` : 'No MQTT activity found in broker log')}>
                                        <Chip
                                            icon={d.mqttConnected ? <MqttIcon sx={{ fontSize: '14px !important' }} /> : <MqttOffIcon sx={{ fontSize: '14px !important' }} />}
                                            label={d.mqttConnected ? 'Online' : 'Offline'}
                                            size="small"
                                            sx={{
                                                fontWeight: 700, borderRadius: '6px', fontSize: '10px',
                                                bgcolor: d.mqttConnected ? '#dcfce7' : '#fee2e2',
                                                color: d.mqttConnected ? '#166534' : '#991b1b'
                                            }} />
                                    </Tooltip>
                                </TableCell>
                                <TableCell><Chip label={d.status || 'UNKNOWN'} size="small" color={d.status === 'RENTAL' ? 'warning' : 'default'} sx={{ fontWeight: 700, borderRadius: '6px', fontSize: '10px' }} /></TableCell>
                                <TableCell>
                                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                        {d.lastDataAt ? new Date(d.lastDataAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'No data'}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Button size="small" variant="contained" startIcon={<SensorIcon />}
                                            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700, fontSize: '11px', px: 1.5 }}
                                            onClick={e => { e.stopPropagation(); onSelect(d.device_id); }}>
                                            Configure
                                        </Button>
                                        <Tooltip title="Check and Update Device Status (MQTT)">
                                            <IconButton size="small" color="primary" 
                                                onClick={async (e) => { 
                                                    e.stopPropagation(); 
                                                    try {
                                                        const res = await (await import('../../services/mewpService')).default.checkAndUpdateDevice(d.device_id);
                                                        alert(res.message || 'Refresh command sent!');
                                                        onRefresh();
                                                    } catch (err) { alert('Failed to send refresh command'); }
                                                }}
                                                sx={{ borderRadius: '8px', bgcolor: 'primary.50', '&:hover': { bgcolor: 'primary.100' } }}>
                                                <RefreshIcon sx={{ fontSize: 18 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        )) : (
                            <TableRow><TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                                <Typography variant="body2" color="text.secondary">No devices found</Typography>
                            </TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

// ─── DEVICE CONFIG SENDER VIEW ───────────────────────────────────────
const DeviceConfigView = ({ deviceId, onBack }) => {
    const [loading, setLoading] = useState(true);
    const [deviceData, setDeviceData] = useState(null);
    const [sending, setSending] = useState(false);
    const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

    // Which config workspace is active. 0=RAW CAN filter, 1=Commands/mode,
    // 2=Signal Mapping (the legacy DBC builder), 3=FOTA/Version (future).
    const [tab, setTab] = useState(0);
    // Bumped after any command is sent so the console reloads immediately.
    const [cmdRefresh, setCmdRefresh] = useState(0);
    const notify = (msg, severity = 'success') => setSnack({ open: true, msg, severity });

    // Editor mode: 'graphic' or 'json'
    const [editMode, setEditMode] = useState('graphic');
    const [jsonContent, setJsonContent] = useState('');

    // New DBC-style global settings
    const [prot, setProt] = useState('11');
    const [baud, setBaud] = useState('500');

    // Config parameters to send
    const [params, setParams] = useState([
        { 
            can_id: '', 
            parameter_name: '', 
            start_bit: 0, 
            bit_length: 16, 
            byte_order: 0, // 0=Big Endian, 1=Little Endian
            data_type: 2,  // 2=unsigned word (defaulting to something common)
            scale_factor: 1, 
            offset_value: 0, 
            unit: '', 
            is_bit: false 
        }
    ]);
    const [customTopic, setCustomTopic] = useState('');
    const [showRawJson, setShowRawJson] = useState(false);

    useEffect(() => {
        const fetchInfo = async () => {
            try {
                setLoading(true);
                const res = await deviceConfigService.getDeviceInfo(deviceId);
                if (res.success) {
                    setDeviceData(res);
                    setCustomTopic(`devices/${res.device.device_id}/config`);
                }
            } catch (err) { console.error(err); }
            finally { setLoading(false); }
        };
        fetchInfo();
    }, [deviceId]);

    const addParam = () => {
        setParams(prev => [...prev, { 
            can_id: '', 
            parameter_name: '', 
            start_bit: 0, 
            bit_length: 16, 
            byte_order: 0, 
            data_type: 0, 
            scale_factor: 1, 
            offset_value: 0, 
            unit: '', 
            is_bit: false 
        }]);
    };

    const removeParam = (index) => {
        setParams(prev => prev.filter((_, i) => i !== index));
    };

    const updateParam = (index, field, value) => {
        setParams(prev => prev.map((p, i) => i === index ? { ...p, [field]: value } : p));
    };

    const buildPayload = () => {
        const validParams = params.filter(p => p.can_id && p.parameter_name);
        
        // Group by CAN ID
        const groups = {};
        validParams.forEach(p => {
            if (!groups[p.can_id]) {
                groups[p.can_id] = { sigtype: {}, bits: {} };
            }
            
            if (p.is_bit) {
                groups[p.can_id].bits[p.parameter_name] = String(p.start_bit);
            } else {
                groups[p.can_id].sigtype[p.parameter_name] = [
                    Number(p.start_bit),
                    Number(p.bit_length),
                    Number(p.byte_order),
                    Number(p.data_type),
                    Number(p.offset_value),
                    Number(p.scale_factor),
                    p.unit || ""
                ];
            }
        });

        const uniqueCanIds = Object.keys(groups);
        const finalJson = {
            prot: String(prot),
            baud: String(baud),
            canids: String(uniqueCanIds.length)
        };

        uniqueCanIds.forEach((id, index) => {
            finalJson[`canid${index + 1}`] = groups[id];
        });

        return finalJson;
    };

    const handleSwitchMode = (mode) => {
        if (mode === 'json') {
            setJsonContent(JSON.stringify(buildPayload(), null, 2));
        } else {
            try {
                const parsed = JSON.parse(jsonContent);
                // Attempt to sync back to graphic state
                if (parsed.prot) setProt(parsed.prot);
                if (parsed.baud) setBaud(parsed.baud);
                
                const newParams = [];
                Object.keys(parsed).forEach(key => {
                    if (key.startsWith('canid')) {
                        const canBlock = parsed[key];
                        // We don't have the literal ID in the JSON keys anymore, 
                        // so we use a placeholder or let the user re-enter it.
                        // However, to keep it simple, we just parse what we can.
                        if (canBlock.sigtype) {
                            Object.entries(canBlock.sigtype).forEach(([name, arr]) => {
                                newParams.push({
                                    can_id: '0x0', // Placeholder
                                    parameter_name: name,
                                    start_bit: arr[0],
                                    bit_length: arr[1],
                                    byte_order: arr[2],
                                    data_type: arr[3],
                                    offset_value: arr[4],
                                    scale_factor: arr[5],
                                    unit: arr[6],
                                    is_bit: false
                                });
                            });
                        }
                        if (canBlock.bits) {
                            Object.entries(canBlock.bits).forEach(([name, bitPos]) => {
                                newParams.push({
                                    can_id: '0x0',
                                    parameter_name: name,
                                    start_bit: bitPos,
                                    bit_length: 1,
                                    is_bit: true,
                                    byte_order: 0,
                                    data_type: 0,
                                    scale_factor: 1,
                                    offset_value: 0,
                                    unit: ''
                                });
                            });
                        }
                    }
                });
                if (newParams.length > 0) setParams(newParams);
            } catch (e) {
                console.warn('Could not sync JSON back to Graphic view:', e.message);
            }
        }
        setEditMode(mode);
    };

    const handleSendConfig = async () => {
        let configPayload;
        if (editMode === 'json') {
            try {
                configPayload = JSON.parse(jsonContent);
            } catch (e) {
                setSnack({ open: true, msg: 'Invalid JSON format', severity: 'error' });
                return;
            }
        } else {
            configPayload = buildPayload();
            if (params.filter(p => p.can_id && p.parameter_name).length === 0) {
                setSnack({ open: true, msg: 'Add at least one valid parameter', severity: 'warning' });
                return;
            }
        }

        try {
            setSending(true);
            const res = await deviceConfigService.sendConfig(
                deviceId,
                configPayload,
                customTopic || null
            );
            if (res.success) {
                setSnack({ open: true, msg: `✅ Config sent to device! Topic: ${res.topic}`, severity: 'success' });
            } else {
                setSnack({ open: true, msg: res.message || 'Failed to send config', severity: 'error' });
            }
        } catch (err) {
            const msg = err.response?.data?.message || err.message || 'Send failed';
            setSnack({ open: true, msg, severity: 'error' });
        } finally { setSending(false); }
    };

    if (loading) return <Box p={4}>{[...Array(3)].map((_, i) => <Skeleton key={i} height={80} sx={{ mb: 2, borderRadius: '16px' }} />)}</Box>;
    if (!deviceData) return <Alert severity="error" sx={{ borderRadius: '16px' }}>Failed to load device info.</Alert>;

    const { device, latestPayload, mqtt } = deviceData;
    const deviceMqttConnected = mqtt?.deviceConnected || false;
    const backendMqttConnected = mqtt?.backendConnected || false;

    return (
        <Box>
            <Button startIcon={<BackIcon />} onClick={onBack} sx={{ mb: 2, textTransform: 'none', fontWeight: 600 }}>Back to Devices</Button>

            {/* Device Header */}
            <Card sx={{ borderRadius: '20px', mb: 3, background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)', color: 'white' }}>
                <CardContent sx={{ p: 3 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                        <Box>
                            <Typography variant="h5" fontWeight={800}>{machineLabel(device)}</Typography>
                            <Typography variant="body2" sx={{ opacity: 0.8, fontFamily: 'monospace' }}>Device ID: {device.device_id}</Typography>
                            <Stack direction="row" spacing={1} mt={1} flexWrap="wrap" useFlexGap>
                                <Chip label={device.device_type} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 700 }} />
                                <Chip label={device.status || 'UNKNOWN'} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 700 }} />
                                <Chip icon={deviceMqttConnected ? <MqttIcon sx={{ color: 'white !important' }} /> : <MqttOffIcon sx={{ color: 'white !important' }} />}
                                    label={deviceMqttConnected ? 'Device Online (Broker Log)' : 'Device Offline (Broker Log)'} size="small"
                                    sx={{ bgcolor: deviceMqttConnected ? 'rgba(34,197,94,0.4)' : 'rgba(239,68,68,0.4)', color: 'white', fontWeight: 700 }} />
                                <Chip icon={backendMqttConnected ? <MqttIcon sx={{ color: 'white !important' }} /> : <MqttOffIcon sx={{ color: 'white !important' }} />}
                                    label={backendMqttConnected ? 'Backend → Broker ✓' : 'Backend → Broker ✗'} size="small"
                                    sx={{ bgcolor: backendMqttConnected ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)', color: 'white', fontWeight: 600 }} />
                                {mqtt?.deviceIp && <Chip label={`IP: ${mqtt.deviceIp}`} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', fontWeight: 600 }} />}
                            </Stack>
                        </Box>
                        <Box textAlign="center">
                            <Typography variant="caption" sx={{ opacity: 0.7, textTransform: 'uppercase', fontSize: '0.6rem' }}>Parameters to Send</Typography>
                            <Typography variant="h3" fontWeight={800}>{params.filter(p => p.can_id && p.parameter_name).length}</Typography>
                        </Box>
                    </Box>
                </CardContent>
            </Card>

            {/* Workspace tabs — RAW CAN is the primary one we're testing first */}
            <Card sx={{ borderRadius: '16px', mb: 3 }}>
                <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto"
                    sx={{ px: 1, '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, minHeight: 52 } }}>
                    <Tab label="RAW CAN Filter" />
                    <Tab label="Commands & Mode" />
                    <Tab label="Signal Mapping" />
                    <Tab label="FOTA / Version" disabled />
                </Tabs>
            </Card>

            {tab === 0 && (
                <RawCanFilterBuilder
                    deviceId={deviceId}
                    disabled={!backendMqttConnected}
                    onNotify={notify}
                    onSent={() => setCmdRefresh(k => k + 1)}
                />
            )}

            {tab === 1 && (
                <DeviceCommandPanel
                    deviceId={deviceId}
                    disabled={!backendMqttConnected}
                    onNotify={notify}
                    onSent={() => setCmdRefresh(k => k + 1)}
                />
            )}

            {/* Command console is shared by the RAW CAN and Commands tabs */}
            {(tab === 0 || tab === 1) && (
                <DeviceCommandConsole deviceId={deviceId} refreshKey={cmdRefresh} />
            )}

            {tab === 2 && (<>
            {/* MQTT Topic */}
            <Card sx={{ borderRadius: '16px', mb: 3, border: '1px solid #e2e8f0' }}>
                <CardContent sx={{ p: 2 }}>
                    <Typography variant="subtitle2" fontWeight={700} mb={1}>📡 MQTT Publish Topic</Typography>
                    <TextField fullWidth value={customTopic} onChange={e => setCustomTopic(e.target.value)}
                        placeholder="devices/{device_id}/config" size="small"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', fontFamily: 'monospace', fontWeight: 600 } }} />
                    <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
                        The device must be subscribed to this topic to receive the configuration
                    </Typography>
                </CardContent>
            </Card>

            {/* Latest Payload from Device */}
            {latestPayload && Object.keys(latestPayload).length > 0 && (
                <Card sx={{ borderRadius: '16px', mb: 3, border: '1px solid #e2e8f0' }}>
                    <CardContent sx={{ p: 2 }}>
                        <Typography variant="subtitle2" fontWeight={700} mb={1}>📥 Latest Data FROM Device (read-only)</Typography>
                        <Box sx={{ bgcolor: '#1e293b', borderRadius: '12px', p: 2, maxHeight: 140, overflow: 'auto' }}>
                            <pre style={{ color: '#22d3ee', fontFamily: 'monospace', fontSize: '12px', margin: 0, whiteSpace: 'pre-wrap' }}>
                                {JSON.stringify(latestPayload, null, 2)}
                            </pre>
                        </Box>
                    </CardContent>
                </Card>
            )}

            {/* Config Parameters Builder */}
            <Card sx={{ borderRadius: '20px', mb: 3 }}>
                <CardContent sx={{ p: 3 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
                        <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="h6" fontWeight={800}>🔧 {editMode === 'graphic' ? 'Graphic Builder' : 'Raw JSON Editor'}</Typography>
                            <Stack direction="row" sx={{ bgcolor: 'grey.100', borderRadius: '12px', p: 0.5, ml: 2 }}>
                                <Button size="small" variant={editMode === 'graphic' ? 'contained' : 'text'}
                                    onClick={() => handleSwitchMode('graphic')}
                                    sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700, px: 2, bgcolor: editMode === 'graphic' ? 'white' : 'transparent', color: editMode === 'graphic' ? 'primary.main' : 'text.secondary', boxShadow: editMode === 'graphic' ? 1 : 0, '&:hover': { bgcolor: editMode === 'graphic' ? 'white' : 'grey.200' } }}>
                                    Graphic
                                </Button>
                                <Button size="small" variant={editMode === 'json' ? 'contained' : 'text'}
                                    onClick={() => handleSwitchMode('json')}
                                    sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700, px: 2, bgcolor: editMode === 'json' ? 'white' : 'transparent', color: editMode === 'json' ? 'primary.main' : 'text.secondary', boxShadow: editMode === 'json' ? 1 : 0, '&:hover': { bgcolor: editMode === 'json' ? 'white' : 'grey.200' } }}>
                                    JSON
                                </Button>
                            </Stack>
                        </Box>
                        
                        {editMode === 'graphic' && (
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                                <FormControl size="small" sx={{ minWidth: 100 }}>
                                    <InputLabel>Protocol</InputLabel>
                                    <Select value={prot} onChange={e => setProt(e.target.value)} label="Protocol" sx={{ borderRadius: '10px' }}>
                                        <MenuItem value="11">11-bit</MenuItem>
                                        <MenuItem value="29">29-bit</MenuItem>
                                    </Select>
                                </FormControl>
                                <FormControl size="small" sx={{ minWidth: 100 }}>
                                    <InputLabel>Baud Rate</InputLabel>
                                    <Select value={baud} onChange={e => setBaud(e.target.value)} label="Baud Rate" sx={{ borderRadius: '10px' }}>
                                        {[125, 250, 500, 1000].map(b => <MenuItem key={b} value={String(b)}>{b}k</MenuItem>)}
                                    </Select>
                                </FormControl>
                                <Button size="small" startIcon={<AddIcon />} variant="contained"
                                    onClick={addParam}
                                    sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}>
                                    Add Parameter
                                </Button>
                            </Stack>
                        )}
                    </Box>

                    {editMode === 'json' ? (
                        <Box>
                            <TextField
                                fullWidth
                                multiline
                                rows={15}
                                value={jsonContent}
                                onChange={e => setJsonContent(e.target.value)}
                                placeholder='{ "prot": "11", ... }'
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '16px',
                                        fontFamily: 'monospace',
                                        fontSize: '13px',
                                        bgcolor: 'grey.50'
                                    }
                                }}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                ⚠️ Manual JSON edits will be used for sending. Switching back to Graphic will attempt to parse changes.
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            {params.map((param, idx) => (
                                <Card key={idx} variant="outlined" sx={{ mb: 2, borderRadius: '16px', border: '1px solid #e2e8f0' }}>
                                    <CardContent sx={{ p: 2 }}>
                                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                                            <Chip label={`#${idx + 1} ${param.parameter_name || 'New Parameter'}`} size="small"
                                                color="primary" variant="outlined" sx={{ fontWeight: 700, borderRadius: '6px' }} />
                                            <IconButton size="small" color="error" onClick={() => removeParam(idx)}><DeleteIcon fontSize="small" /></IconButton>
                                        </Box>
                                        <Grid container spacing={1.5}>
                                            <Grid item xs={12} md={3}>
                                                <TextField fullWidth label="CAN ID (unit handle)" value={param.can_id} size="small" placeholder="0x18FF50E5"
                                                    onChange={e => updateParam(idx, 'can_id', e.target.value)}
                                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
                                            </Grid>
                                            <Grid item xs={12} md={3}>
                                                <TextField fullWidth label="Signal Name" value={param.parameter_name} size="small"
                                                    onChange={e => updateParam(idx, 'parameter_name', e.target.value)}
                                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
                                            </Grid>
                                            <Grid item xs={6} md={1.5}>
                                                <TextField fullWidth label="Start Bit" value={param.start_bit} size="small" type="number"
                                                    onChange={e => updateParam(idx, 'start_bit', e.target.value)}
                                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
                                            </Grid>
                                            <Grid item xs={6} md={1.5}>
                                                <TextField fullWidth label="Bit Length" value={param.bit_length} size="small" type="number"
                                                    disabled={param.is_bit}
                                                    onChange={e => updateParam(idx, 'bit_length', e.target.value)}
                                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
                                            </Grid>
                                            <Grid item xs={12} md={3}>
                                                <Stack direction="row" spacing={1} alignItems="center" height="100%">
                                                    <Typography variant="caption" fontWeight={700}>IS BIT?</Typography>
                                                    <Switch checked={param.is_bit} onChange={e => {
                                                        const val = e.target.checked;
                                                        updateParam(idx, 'is_bit', val);
                                                        if (val) updateParam(idx, 'bit_length', 1);
                                                    }} />
                                                </Stack>
                                            </Grid>
                                            
                                            {!param.is_bit && (
                                                <>
                                                    <Grid item xs={6} md={2}>
                                                        <FormControl fullWidth size="small">
                                                            <InputLabel>Byte Order</InputLabel>
                                                            <Select value={param.byte_order} onChange={e => updateParam(idx, 'byte_order', e.target.value)}
                                                                label="Byte Order" sx={{ borderRadius: '10px' }}>
                                                                <MenuItem value={1}>Little Endian</MenuItem>
                                                                <MenuItem value={0}>Big Endian</MenuItem>
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                    <Grid item xs={6} md={3}>
                                                        <FormControl fullWidth size="small">
                                                            <InputLabel>Data Type</InputLabel>
                                                            <Select value={param.data_type} onChange={e => updateParam(idx, 'data_type', e.target.value)}
                                                                label="Data Type" sx={{ borderRadius: '10px' }}>
                                                                <MenuItem value={0}>0 (Unsigned Byte)</MenuItem>
                                                                <MenuItem value={1}>1 (Signed Byte)</MenuItem>
                                                                <MenuItem value={2}>2 (Unsigned Word)</MenuItem>
                                                                <MenuItem value={3}>3 (Signed Word)</MenuItem>
                                                                <MenuItem value={4}>4 (Unsigned Int)</MenuItem>
                                                                <MenuItem value={5}>5 (Signed Int)</MenuItem>
                                                                <MenuItem value={6}>6 (Float)</MenuItem>
                                                                <MenuItem value={7}>7 (String)</MenuItem>
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                    <Grid item xs={4} md={1.5}>
                                                        <TextField fullWidth label="Offset" value={param.offset_value} size="small" type="number"
                                                            onChange={e => updateParam(idx, 'offset_value', e.target.value)}
                                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
                                                    </Grid>
                                                    <Grid item xs={4} md={1.5}>
                                                        <TextField fullWidth label="Scale" value={param.scale_factor} size="small" type="number"
                                                            onChange={e => updateParam(idx, 'scale_factor', e.target.value)}
                                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
                                                    </Grid>
                                                    <Grid item xs={4} md={2}>
                                                        <TextField fullWidth label="Unit" value={param.unit} size="small" placeholder="mV/RPM"
                                                            onChange={e => updateParam(idx, 'unit', e.target.value)}
                                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
                                                    </Grid>
                                                </>
                                            )}
                                        </Grid>
                                        {!param.is_bit && (
                                            <Alert severity="info" icon={false} sx={{ mt: 1.5, borderRadius: '10px', py: 0.5, fontSize: '12px' }}>
                                                <strong>Formula:</strong> <code style={{ fontWeight: 700 }}>readable = (raw × {param.scale_factor || 1}) + ({param.offset_value || 0})</code>
                                                {param.unit ? <span> → <strong>{param.unit}</strong></span> : ''}
                                            </Alert>
                                        )}
                                    </CardContent>
                                </Card>
                            ))}
                            {params.length === 0 && (
                                <Box textAlign="center" py={6}>
                                    <SensorIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                                    <Typography color="text.secondary" fontWeight={600}>No parameters added yet</Typography>
                                    <Button startIcon={<AddIcon />} variant="contained" onClick={addParam} sx={{ mt: 2, borderRadius: '12px', textTransform: 'none', fontWeight: 700 }}>
                                        Add First Parameter
                                    </Button>
                                </Box>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>

            {/* JSON Preview */}
            {showRawJson && (
                <Card sx={{ borderRadius: '16px', mb: 3, border: '1px solid #e2e8f0' }}>
                    <CardContent sx={{ p: 2 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                            <Typography variant="subtitle2" fontWeight={700}>📤 JSON Payload (what will be sent via MQTT)</Typography>
                            <Tooltip title="Copy JSON">
                                <IconButton size="small" onClick={() => { navigator.clipboard.writeText(JSON.stringify({ type: 'config', device_id: device.device_id, config: buildPayload(), sent_at: new Date().toISOString() }, null, 2)); setSnack({ open: true, msg: 'JSON copied!', severity: 'info' }); }}>
                                    <CopyIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        <Box sx={{ bgcolor: '#1e293b', borderRadius: '12px', p: 2, maxHeight: 300, overflow: 'auto' }}>
                            <pre style={{ color: '#a5f3fc', fontFamily: 'monospace', fontSize: '12px', margin: 0, whiteSpace: 'pre-wrap' }}>
                                {JSON.stringify({ type: 'config', device_id: device.device_id, equipment_id: device.equipment_id, config: buildPayload(), sent_at: new Date().toISOString() }, null, 2)}
                            </pre>
                        </Box>
                    </CardContent>
                </Card>
            )}

            {/* Send Button */}
            <Card sx={{ borderRadius: '20px', background: backendMqttConnected ? 'linear-gradient(135deg, #059669 0%, #10b981 100%)' : '#94a3b8', color: 'white' }}>
                <CardContent sx={{ p: 3, textAlign: 'center' }}>
                    <Button fullWidth variant="contained" size="large" startIcon={<SendIcon />}
                        disabled={!backendMqttConnected || sending || (editMode === 'graphic' && params.filter(p => p.can_id && p.parameter_name).length === 0)}
                        onClick={handleSendConfig}
                        sx={{
                            bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 800, fontSize: '16px', borderRadius: '16px', py: 1.5, textTransform: 'none',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                            '&.Mui-disabled': { color: 'rgba(255,255,255,0.5)' }
                        }}>
                        {sending ? 'Sending...' : backendMqttConnected ? `Send Config to ${machineLabel(device)}` : 'Backend not connected to broker — Cannot Send'}
                    </Button>
                    <Typography variant="caption" sx={{ opacity: 0.8, mt: 1, display: 'block' }}>
                        Config will be published to: <code style={{ fontWeight: 700 }}>{customTopic}</code>
                    </Typography>
                </CardContent>
            </Card>
            </>)}

            <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(p => ({ ...p, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity={snack.severity} onClose={() => setSnack(p => ({ ...p, open: false }))}
                    sx={{ borderRadius: '12px', fontWeight: 600 }}>
                    {snack.msg}
                </Alert>
            </Snackbar>
        </Box>
    );
};

// ─── MAIN PAGE ───────────────────────────────────────────────────────
const DeviceConfigPage = () => {
    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedDevice, setSelectedDevice] = useState(null);
    const [brokerStatus, setBrokerStatus] = useState(null);

    const fetchDevices = async () => {
        try {
            setLoading(true);
            const res = await deviceConfigService.getDevices();
            if (res.success) {
                setDevices(res.data || []);
                setBrokerStatus(res.brokerStatus || null);
            }
        } catch (err) { console.error(err); setDevices([]); }
        finally { setLoading(false); }
    };

    useEffect(() => { fetchDevices(); }, []);

    return (
        <Box p={3} className="animate-fade-in">
            {selectedDevice ? (
                <DeviceConfigView deviceId={selectedDevice} onBack={() => setSelectedDevice(null)} />
            ) : (
                <DeviceListView devices={devices} loading={loading} onSelect={setSelectedDevice}
                    onRefresh={fetchDevices} brokerStatus={brokerStatus} />
            )}
        </Box>
    );
};

export default DeviceConfigPage;
